import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { VendorDetail } from '@/components/analytics';
import { useVendorSummary, useCanvasserStats, useContactAnalytics, useVendorPerformance } from '@/hooks/queries';
import type { CanvasserAnalyticsFilters } from '@/lib/queryKeys';

export const Route = createFileRoute('/app/analytics/vendors/$vendorId')({
  component: VendorDetailRoute,
});

function VendorDetailRoute() {
  const { vendorId } = Route.useParams();
  const filters = Route.useSearch() as CanvasserAnalyticsFilters;
  const navigate = useNavigate();

  const { data: vendorSummary, isLoading: isLoadingSummary } = useVendorSummary({
    ...filters,
    vendorId,
  });
  const { data: canvasserStatsResponse, isLoading: isLoadingStats } = useCanvasserStats({
    ...filters,
    vendorId,
  });
  const canvasserStats = canvasserStatsResponse?.items;
  const benchmarks = canvasserStatsResponse?.benchmarks;

  // VDA-002: Vendor performance trend
  const { data: vendorPerformance, isLoading: isLoadingVendorPerformance } =
    useVendorPerformance(vendorId, { dateFrom: filters.dateFrom, dateTo: filters.dateTo });

  // VDA-003: Vendor contact outcomes (vendor-scoped) and campaign-wide (for comparison)
  const { data: vendorContactData, isLoading: isLoadingVendorContacts } =
    useContactAnalytics(undefined, vendorId);
  const { data: campaignContactData } = useContactAnalytics();

  const handleBack = () => {
    navigate({
      to: '/app/analytics',
      search: filters,
    });
  };

  const handleCanvasserClick = (userId: string) => {
    navigate({
      to: '/app/analytics/canvassers/$userId',
      params: { userId },
      search: filters,
    });
  };

  return (
    <VendorDetail
      vendorId={vendorId}
      vendorSummary={vendorSummary ?? []}
      canvasserStats={canvasserStats ?? []}
      isLoadingSummary={isLoadingSummary}
      isLoadingStats={isLoadingStats}
      onBack={handleBack}
      onCanvasserClick={handleCanvasserClick}
      benchmarks={benchmarks}
      vendorContactOutcomes={vendorContactData?.contactsByStatus}
      campaignContactOutcomes={campaignContactData?.contactsByStatus}
      isLoadingContactOutcomes={isLoadingVendorContacts}
      vendorPerformance={vendorPerformance}
      isLoadingVendorPerformance={isLoadingVendorPerformance}
    />
  );
}
